/**
 * Access Control Service
 * Handles all API calls related to access control and permissions
 * 
 * @author JGS Tech Team
 * @version 1.0
 */

import api from '../helpers/axios';
import type {
    Permission,
    PermissionPayload,
    UserPermissionsResponse,
    PermissionOverridePayload,
    RolePermissionPayload,
    UserPermissionOverride,
    PermissionAuditLog,
    RolePermissionMatrix,
    GroupedPermissions,
    AccessControlUser,
    PermissionValidationResult,
    BulkPermissionValidationResult,
} from '../types/accessControl';

// Base URLs
const BASE_URL = '/api';
const ADMIN_PERMISSIONS_URL = `${BASE_URL}/admin/permissions`;
const ADMIN_ACCESS_CONTROL_URL = `${BASE_URL}/admin/access-control`;
const AUTH_URL = `${BASE_URL}/auth`;

/**
 * Access Control Service class
 * Singleton pattern for consistent API access
 */
class AccessControlService {
    private static instance: AccessControlService;

    private constructor() {}

    public static getInstance(): AccessControlService {
        if (!AccessControlService.instance) {
            AccessControlService.instance = new AccessControlService();
        }
        return AccessControlService.instance;
    }

    // ==================== Current User Permissions ====================

    /**
     * Get current user's permissions
     */
    async getMyPermissions(): Promise<UserPermissionsResponse> {
        const response = await api.get<UserPermissionsResponse>(`${AUTH_URL}/my-permissions`);
        return response.data;
    }

    /**
     * Validate if current user has a specific permission
     */
    async validateMyPermission(permissionCode: string): Promise<PermissionValidationResult> {
        const response = await api.get<PermissionValidationResult>(
            `${AUTH_URL}/validate-permission/${permissionCode}`
        );
        return response.data;
    }

    /**
     * Validate if current user has multiple permissions
     */
    async validateMyPermissions(
        permissionCodes: string[],
        mode: 'any' | 'all' = 'any'
    ): Promise<BulkPermissionValidationResult> {
        const response = await api.post<BulkPermissionValidationResult>(
            `${AUTH_URL}/validate-permissions`,
            permissionCodes,
            { params: { mode } }
        );
        return response.data;
    }

    /**
     * Get current user's roles
     */
    async getMyRoles(): Promise<{ roles: string[] }> {
        const response = await api.get<{ roles: string[] }>(`${AUTH_URL}/my-roles`);
        return response.data;
    }

    /**
     * Check if current user has a specific role
     */
    async hasRole(role: string): Promise<{ role: string; hasRole: boolean }> {
        const response = await api.get<{ role: string; hasRole: boolean }>(
            `${AUTH_URL}/has-role/${role}`
        );
        return response.data;
    }

    // ==================== Permission CRUD (Admin) ====================

    /**
     * Get all active permissions
     */
    async getAllPermissions(): Promise<Permission[]> {
        const response = await api.get<Permission[]>(ADMIN_PERMISSIONS_URL);
        return response.data;
    }

    /**
     * Get permission by ID
     */
    async getPermissionById(permissionId: string): Promise<Permission> {
        const response = await api.get<Permission>(`${ADMIN_PERMISSIONS_URL}/${permissionId}`);
        return response.data;
    }

    /**
     * Get permission by code
     */
    async getPermissionByCode(permissionCode: string): Promise<Permission> {
        const response = await api.get<Permission>(
            `${ADMIN_PERMISSIONS_URL}/by-code/${permissionCode}`
        );
        return response.data;
    }

    /**
     * Create a new permission
     */
    async createPermission(payload: PermissionPayload): Promise<Permission> {
        const response = await api.post<Permission>(ADMIN_PERMISSIONS_URL, payload);
        return response.data;
    }

    /**
     * Update a permission
     */
    async updatePermission(permissionId: string, payload: PermissionPayload): Promise<Permission> {
        const response = await api.put<Permission>(
            `${ADMIN_PERMISSIONS_URL}/${permissionId}`,
            payload
        );
        return response.data;
    }

    /**
     * Delete a permission
     */
    async deletePermission(permissionId: string): Promise<{ success: boolean; message: string }> {
        const response = await api.delete<{ success: boolean; message: string }>(
            `${ADMIN_PERMISSIONS_URL}/${permissionId}`
        );
        return response.data;
    }

    // ==================== Permission Categories ====================

    /**
     * Get all permission categories
     */
    async getAllCategories(): Promise<string[]> {
        const response = await api.get<string[]>(`${ADMIN_PERMISSIONS_URL}/categories`);
        return response.data;
    }

    /**
     * Get permissions by category
     */
    async getPermissionsByCategory(category: string): Promise<Permission[]> {
        const response = await api.get<Permission[]>(
            `${ADMIN_PERMISSIONS_URL}/category/${category}`
        );
        return response.data;
    }

    /**
     * Get permissions grouped by category
     */
    async getPermissionsGroupedByCategory(): Promise<GroupedPermissions> {
        const response = await api.get<GroupedPermissions>(`${ADMIN_PERMISSIONS_URL}/grouped`);
        return response.data;
    }

    /**
     * Search permissions
     */
    async searchPermissions(query: string): Promise<Permission[]> {
        const response = await api.get<Permission[]>(`${ADMIN_PERMISSIONS_URL}/search`, {
            params: { query },
        });
        return response.data;
    }

    // ==================== Role-Permission Management ====================

    /**
     * Get role-permission matrix
     */
    async getRolePermissionMatrix(): Promise<RolePermissionMatrix> {
        const response = await api.get<RolePermissionMatrix>(
            `${ADMIN_PERMISSIONS_URL}/roles/matrix`
        );
        return response.data;
    }

    /**
     * Get permissions for a specific role
     */
    async getRolePermissions(role: string): Promise<string[]> {
        const response = await api.get<string[]>(`${ADMIN_PERMISSIONS_URL}/roles/${role}`);
        return response.data;
    }

    /**
     * Update permissions for a role
     */
    async updateRolePermissions(
        role: string,
        payload: RolePermissionPayload
    ): Promise<{ success: boolean; message: string }> {
        const response = await api.put<{ success: boolean; message: string }>(
            `${ADMIN_PERMISSIONS_URL}/roles/${role}`,
            payload
        );
        return response.data;
    }

    /**
     * Add permission to role
     */
    async addPermissionToRole(
        role: string,
        permissionCode: string
    ): Promise<{ success: boolean; message: string }> {
        const response = await api.post<{ success: boolean; message: string }>(
            `${ADMIN_PERMISSIONS_URL}/roles/${role}/add`,
            null,
            { params: { permissionCode } }
        );
        return response.data;
    }

    /**
     * Remove permission from role
     */
    async removePermissionFromRole(
        role: string,
        permissionCode: string
    ): Promise<{ success: boolean; message: string }> {
        const response = await api.delete<{ success: boolean; message: string }>(
            `${ADMIN_PERMISSIONS_URL}/roles/${role}/remove`,
            { params: { permissionCode } }
        );
        return response.data;
    }

    // ==================== User Access Control ====================

    /**
     * Get complete permissions for a user
     */
    async getUserPermissions(userId: string): Promise<UserPermissionsResponse> {
        const response = await api.get<UserPermissionsResponse>(
            `${ADMIN_ACCESS_CONTROL_URL}/users/${userId}/permissions`
        );
        return response.data;
    }

    /**
     * Get user's permission overrides
     */
    async getUserOverrides(userId: string): Promise<UserPermissionOverride[]> {
        const response = await api.get<UserPermissionOverride[]>(
            `${ADMIN_ACCESS_CONTROL_URL}/users/${userId}/overrides`
        );
        return response.data;
    }

    /**
     * Grant a permission to a user
     */
    async grantPermission(
        userId: string,
        payload: PermissionOverridePayload
    ): Promise<{ success: boolean; message: string }> {
        const response = await api.post<{ success: boolean; message: string }>(
            `${ADMIN_ACCESS_CONTROL_URL}/users/${userId}/grant`,
            payload
        );
        return response.data;
    }

    /**
     * Revoke a permission from a user
     */
    async revokePermission(
        userId: string,
        payload: PermissionOverridePayload
    ): Promise<{ success: boolean; message: string }> {
        const response = await api.post<{ success: boolean; message: string }>(
            `${ADMIN_ACCESS_CONTROL_URL}/users/${userId}/revoke`,
            payload
        );
        return response.data;
    }

    /**
     * Remove a permission override
     */
    async removeOverride(
        userId: string,
        permissionCode: string
    ): Promise<{ success: boolean; message: string }> {
        const response = await api.delete<{ success: boolean; message: string }>(
            `${ADMIN_ACCESS_CONTROL_URL}/users/${userId}/override`,
            { params: { permissionCode } }
        );
        return response.data;
    }

    /**
     * Remove all overrides for a user
     */
    async removeAllOverrides(userId: string): Promise<{ success: boolean; message: string }> {
        const response = await api.delete<{ success: boolean; message: string }>(
            `${ADMIN_ACCESS_CONTROL_URL}/users/${userId}/overrides/all`
        );
        return response.data;
    }

    /**
     * Validate if user has a specific permission
     */
    async validateUserPermission(
        userId: string,
        permissionCode: string
    ): Promise<{ userId: string; permissionCode: string; hasPermission: boolean }> {
        const response = await api.post<{
            userId: string;
            permissionCode: string;
            hasPermission: boolean;
        }>(`${ADMIN_ACCESS_CONTROL_URL}/users/${userId}/validate`, null, {
            params: { permissionCode },
        });
        return response.data;
    }

    /**
     * Validate multiple permissions for a user
     */
    async validateUserPermissions(
        userId: string,
        permissionCodes: string[],
        mode: 'any' | 'all' = 'any'
    ): Promise<BulkPermissionValidationResult> {
        const response = await api.post<BulkPermissionValidationResult>(
            `${ADMIN_ACCESS_CONTROL_URL}/users/${userId}/validate-many`,
            permissionCodes,
            { params: { mode } }
        );
        return response.data;
    }

    // ==================== Cache Management ====================

    /**
     * Invalidate permission cache for a user
     */
    async invalidateUserCache(userId: string): Promise<{ success: boolean; message: string }> {
        const response = await api.post<{ success: boolean; message: string }>(
            `${ADMIN_ACCESS_CONTROL_URL}/users/${userId}/invalidate-cache`
        );
        return response.data;
    }

    /**
     * Invalidate all permission caches
     */
    async invalidateAllCaches(): Promise<{ success: boolean; message: string }> {
        const response = await api.post<{ success: boolean; message: string }>(
            `${ADMIN_ACCESS_CONTROL_URL}/invalidate-all-caches`
        );
        return response.data;
    }

    // ==================== Audit Logs ====================

    /**
     * Get all audit logs with filtering
     * Backend returns Spring Page object, so we extract the content array
     */
    async getAuditLogs(params: {
        page?: number;
        size?: number;
        action?: string;
        username?: string;
        fromDate?: string;
        toDate?: string;
    } = {}): Promise<PermissionAuditLog[]> {
        const response = await api.get<{
            content: PermissionAuditLog[];
            totalElements: number;
            totalPages: number;
            size: number;
            number: number;
        }>(
            `${ADMIN_ACCESS_CONTROL_URL}/audit-logs`,
            { params }
        );
        // Extract content array from Spring Page response
        return response.data?.content || [];
    }

    /**
     * Get recent audit logs
     */
    async getRecentAuditLogs(limit: number = 50): Promise<PermissionAuditLog[]> {
        const response = await api.get<PermissionAuditLog[]>(
            `${ADMIN_ACCESS_CONTROL_URL}/audit-logs/recent`,
            { params: { limit } }
        );
        return response.data;
    }

    /**
     * Get audit logs for a user
     * Backend returns Spring Page object, so we extract the content array
     */
    async getUserAuditLogs(
        userId: string,
        page: number = 0,
        size: number = 20
    ): Promise<PermissionAuditLog[]> {
        const response = await api.get<{
            content: PermissionAuditLog[];
            totalElements: number;
            totalPages: number;
            size: number;
            number: number;
        }>(
            `${ADMIN_ACCESS_CONTROL_URL}/audit-logs/user/${userId}`,
            { params: { page, size } }
        );
        // Extract content array from Spring Page response
        return response.data?.content || [];
    }

    /**
     * Get audit statistics
     */
    async getAuditStatistics(fromDate?: string, toDate?: string): Promise<Record<string, number>> {
        const response = await api.get<Record<string, number>>(
            `${ADMIN_ACCESS_CONTROL_URL}/audit-logs/statistics`,
            { params: { fromDate, toDate } }
        );
        return response.data;
    }

    // ==================== Health Check ====================

    /**
     * Check permission service health
     */
    async checkHealth(): Promise<{ status: string; service: string; timestamp: number }> {
        const response = await api.get<{ status: string; service: string; timestamp: number }>(
            `${ADMIN_PERMISSIONS_URL}/health`
        );
        return response.data;
    }

    /**
     * Get API info
     */
    async getApiInfo(): Promise<{ service: string; version: string; endpoints: string[] }> {
        const response = await api.get<{
            service: string;
            version: string;
            endpoints: string[];
        }>(`${ADMIN_PERMISSIONS_URL}/api-info`);
        return response.data;
    }
}

// Export singleton instance
export const accessControlService = AccessControlService.getInstance();

// Export class for testing
export { AccessControlService };

// Default export
export default accessControlService;
